/**
 * Favorites — per-content-type bookmarks stored in localStorage, scoped to the active profile.
 * Each entry: {stream_url, name, logo_url, content_type, group, added_at}
 * Keyed by stream_url within a content_type bucket.
 */
import { getActiveProfileId } from "./profiles";
import { scheduleProfileCacheSync } from "./profileCacheSync";

const BASE_KEY = "vupp_favorites_v1";

function storageKey() {
  const pid = getActiveProfileId();
  return pid ? `${BASE_KEY}__${pid}` : BASE_KEY;
}

function readAll() {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

function writeAll(obj) {
  localStorage.setItem(storageKey(), JSON.stringify(obj));
  // v1.3.36 — debounced sync of profiles/favorites/history/settings to backend.
  scheduleProfileCacheSync();
}

export function getFavorites(contentType) {
  const all = readAll();
  return all[contentType] || [];
}

export function isFavorited(contentType, streamUrl) {
  return getFavorites(contentType).some((f) => f.stream_url === streamUrl);
}

export function toggleFavorite(contentType, item) {
  const all = readAll();
  const list = all[contentType] || [];
  const idx = list.findIndex((f) => f.stream_url === item.stream_url);
  if (idx >= 0) {
    list.splice(idx, 1);
  } else {
    // v1.4.71 — Persist `series_id` + `series_name` when the caller
    // supplies them (series tile in Browse.jsx passes both). Without
    // these, the Home page's "Recently added episodes (Favorites)"
    // rail can't match episodes to a favourited show because the
    // stream_url uses a synthetic "series://<id>" key that doesn't
    // equal the human-readable series_name on the episode rows.
    list.unshift({
      stream_url: item.stream_url,
      name: item.name,
      logo_url: item.logo_url || null,
      content_type: contentType,
      group: item.group || null,
      series_id: item.series_id || null,
      series_name: item.series_name || null,
      added_at: new Date().toISOString(),
    });
  }
  all[contentType] = list;
  writeAll(all);
  return idx < 0; // returns new state: true if now favorited
}

export function favoritesCount(contentType) {
  return getFavorites(contentType).length;
}

export function clearFavorites(contentType) {
  const all = readAll();
  delete all[contentType];
  writeAll(all);
}

// Helper for content types that support favorites.
export const FAVORITABLE_TYPES = ["live_tv", "movie", "series"];
export function isFavoritable(contentType) {
  return FAVORITABLE_TYPES.includes(contentType);
}
